/**
 * 사용 설명서 목차와 검색.
 *
 * **이 파일은 `nav.ts` 를 가져오지 않는다.** `nav.ts` 가 헤더 제목을 찾으려고 이 목차를
 * 읽기 때문에, 거꾸로 가져오면 순환이 된다. 메뉴 아이콘을 찾는 일은 화면(`Manual.tsx`)이 한다.
 */
import { ACCOUNT, USERS } from './chapters/admin.js';
import { AGENT_DEVICE, AGENT_INSTALL, AGENT_UPDATE } from './chapters/agent.js';
import { CONTENT, PLAYLISTS } from './chapters/content.js';
import { REPORTS } from './chapters/records.js';
import { CAMPAIGNS, PRIORITY, SCHEDULES } from './chapters/scheduling.js';
import { DASHBOARD, DIRECT_SEND, DISPLAYS, DISPLAY_DETAIL, INTEGRATED_MONITORING, MONITORING } from './chapters/screens.js';
import { GETTING_STARTED, GLOSSARY, TROUBLESHOOTING } from './chapters/start.js';
import { GUIDES } from './chapters/guides.js';
import { HUB } from './chapters/hub.js';
import { SETTINGS } from './chapters/settings.js';
import type { ManualBlock, ManualChapter, ManualGroup, ManualSection } from './types.js';

/**
 * 묶음 순서가 곧 처음 쓰는 사람의 작업 순서다 — 기기를 깔고, 화면을 보고, 편성하고,
 * 기록과 계정을 관리한다. 막혔을 때 찾는 장은 맨 뒤에 둔다.
 */
/**
 * 장 앞에 화면 가이드를 끼운다 — 그림으로 둘러보기, 상황별 따라 하기, 그다음 글로 된 참고.
 * 처음 온 사람은 앞의 둘로 충분하고, 자세히 알고 싶은 사람은 계속 내려 읽는다.
 */
function withGuide(chapter: ManualChapter): ManualChapter {
  const guide = GUIDES[chapter.slug];
  if (!guide) return chapter;
  const front = [guide.tour, guide.scenarios].filter((section): section is ManualSection => Boolean(section));
  return { ...chapter, sections: [...front, ...chapter.sections] };
}

export const MANUAL_GROUPS: readonly ManualGroup[] = [
  { title: '시작하기', chapters: [GETTING_STARTED] },
  { title: '화면 기기 (에이전트)', chapters: [AGENT_INSTALL, AGENT_DEVICE, AGENT_UPDATE] },
  { title: '화면 보기', chapters: [DASHBOARD, DISPLAYS, DISPLAY_DETAIL, MONITORING, INTEGRATED_MONITORING, HUB] },
  { title: '콘텐츠와 편성', chapters: [CONTENT, PLAYLISTS, SCHEDULES, CAMPAIGNS, DIRECT_SEND, PRIORITY] },
  { title: '기록과 관리', chapters: [REPORTS, USERS, SETTINGS, ACCOUNT] },
  { title: '도움말', chapters: [TROUBLESHOOTING, GLOSSARY] },
].map((group) => ({ ...group, chapters: group.chapters.map(withGuide) }));

/** 이전·다음 장은 이 순서를 따른다. */
export const MANUAL_CHAPTERS: readonly ManualChapter[] = MANUAL_GROUPS.flatMap((group) => group.chapters);

/** 첫 화면의 다섯 단계. 누르면 그 일을 설명하는 절로 바로 간다. */
export const QUICK_START: ReadonlyArray<{ title: string; body: string; menu: string; to: string }> = [
  { title: '기기 준비', body: '화면으로 쓸 PC·TV 박스에 공급사가 재생 프로그램을 설치합니다.', menu: '공급사 설치', to: '/manual/agent-install#devices' },
  { title: '화면 등록', body: '기기 화면에 뜬 6자리 코드를 콘솔에 넣습니다.', menu: '화면', to: '/manual/agent-install#pair' },
  { title: '콘텐츠 올리기', body: '이미지·영상을 올리거나 템플릿으로 공지를 만듭니다.', menu: '콘텐츠', to: '/manual/content#upload' },
  { title: '플레이리스트 만들기', body: '재생 순서와 한 장당 노출 시간을 정합니다.', menu: '플레이리스트', to: '/manual/playlists#create' },
  { title: '화면에 배정', body: '어느 화면에 무엇을 재생할지 정하면 약 30초 뒤 나옵니다.', menu: '화면', to: '/manual/displays#assign' },
];

export interface ScreenGuideEntry {
  chapter: ManualChapter;
  /** 카드 썸네일 — 그 장의 첫 화면 그림. */
  shot: string;
  screens: number;
  markers: number;
  scenarios: number;
}

/**
 * 첫 화면의 "화면으로 보는 가이드" 카드 목록.
 *
 * 화면 가이드는 장 안에만 있어서, 첫 화면만 보는 사람에게는 새 가이드가 있다는 것조차
 * 드러나지 않았다(배포하고 "안 보인다" 는 말을 들었다). 장 순서대로, 둘러보기가 있는 장만.
 */
export function screenGuideEntries(): ScreenGuideEntry[] {
  return MANUAL_CHAPTERS.flatMap((chapter) => {
    const screens = (chapter.sections.find((section) => section.id === 'tour')?.blocks ?? []).filter(
      (block): block is Extract<ManualBlock, { kind: 'screen' }> => block.kind === 'screen',
    );
    const first = screens[0];
    if (!first) return [];
    const scenarios = (chapter.sections.find((section) => section.id === 'scenarios')?.blocks ?? []).filter(
      (block) => block.kind === 'scenario',
    ).length;
    return [
      {
        chapter,
        shot: first.shot,
        screens: screens.length,
        markers: screens.reduce((sum, block) => sum + block.markers.length, 0),
        scenarios,
      },
    ];
  });
}

export function chapterBySlug(slug: string): ManualChapter | undefined {
  return MANUAL_CHAPTERS.find((chapter) => chapter.slug === slug);
}

/* ────────────────────────────── 검색 ────────────────────────────── */

/** 강조 표시를 걷어낸 글. 검색과 발췌는 사람이 읽는 글자로 한다. */
export function plainText(text: string): string {
  return text.replace(/\*\*|\[\[|\]\]/g, '');
}

function blockText(block: ManualBlock): string {
  switch (block.kind) {
    case 'text':
      return block.text;
    case 'steps':
      return [block.title, ...block.steps.flatMap((step) => [step.title, step.body ?? ''])].join(' ');
    case 'callout':
      return `${block.title} ${block.text}`;
    case 'legend':
      return [block.title, ...block.items.flatMap((item) => [item.label, item.meaning])].join(' ');
    case 'actions':
      return [block.title, ...block.items.flatMap((item) => [item.label, item.text])].join(' ');
    case 'table':
      return [...block.header, ...block.rows.flat()].join(' ');
    case 'figure':
      return block.caption;
    case 'faq':
      return block.items.flatMap((item) => [item.q, item.a]).join(' ');
    case 'screen':
      return [block.title, ...block.markers.flatMap((marker) => [marker.label, marker.text])].join(' ');
    case 'scenario':
      return [block.title, block.situation, ...block.steps.map((step) => step.text), block.result].join(' ');
  }
}

interface IndexEntry {
  chapter: ManualChapter;
  section: ManualSection;
  chapterTitle: string;
  sectionTitle: string;
  body: string;
}

let index: IndexEntry[] | null = null;

/** 처음 검색할 때 한 번만 만든다 — 설명서를 열기만 하고 검색하지 않는 사람이 대부분이다. */
function searchIndex(): IndexEntry[] {
  index ??= MANUAL_CHAPTERS.flatMap((chapter) =>
    chapter.sections.map((section) => ({
      chapter,
      section,
      chapterTitle: chapter.title.toLowerCase(),
      sectionTitle: section.title.toLowerCase(),
      body: plainText(section.blocks.map(blockText).join(' ')).replace(/\s+/g, ' '),
    })),
  );
  return index;
}

export interface ManualHit {
  chapter: ManualChapter;
  section: ManualSection;
  /** 첫 검색어 둘레의 한두 줄. 제목에만 걸렸으면 본문 앞부분. */
  snippet: string;
}

/**
 * 낱말마다 **모두** 들어 있는 절을 찾는다.
 *
 * 형태소 분석은 하지 않는다. "영상 올리기" 처럼 명사 위주로 찾는 사람이 대부분이고,
 * 설명서는 수백 절 규모라 부분 문자열 비교로 충분히 빠르다. 제목에 걸린 절을 앞에 둔다 —
 * "스케줄" 을 찾은 사람에게 스케줄 장이 캠페인 장의 한 문장보다 먼저 보여야 한다.
 *
 * **절 제목이 장 제목보다 무겁다.** 장 제목은 그 장의 모든 절이 나눠 갖는다. "정비 모드" 를
 * 찾았는데 "기기 화면과 정비 모드" 장의 첫 절(기기 안내 문구)이 정비 모드 절보다 앞서면 안 된다.
 */
export function searchManual(query: string): ManualHit[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];

  return searchIndex()
    .map((entry) => {
      const body = entry.body.toLowerCase();
      const inTitles = (term: string): boolean => entry.sectionTitle.includes(term) || entry.chapterTitle.includes(term);
      if (!terms.every((term) => inTitles(term) || body.includes(term))) return null;
      const score = terms.reduce(
        (sum, term) => sum + (entry.sectionTitle.includes(term) ? 2 : 0) + (entry.chapterTitle.includes(term) ? 1 : 0),
        0,
      );
      return { entry, score, at: body.indexOf(terms[0] ?? '') };
    })
    .filter((hit): hit is NonNullable<typeof hit> => hit !== null)
    .sort((a, b) => b.score - a.score)
    .slice(0, 30)
    .map(({ entry, at }) => {
      const start = Math.max(0, at - 30);
      const slice = entry.body.slice(start, start + 120);
      return {
        chapter: entry.chapter,
        section: entry.section,
        snippet: `${start > 0 ? '…' : ''}${slice}${start + 120 < entry.body.length ? '…' : ''}`,
      };
    });
}
